import { useTranslation } from "react-i18next";
import { PenLine, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { RecordingPlayer } from "../recording/RecordingPlayer";

interface AnnotationEpilogueScreenProps {
  noteText: string;
  recordings?: { audio?: any; video?: any } | null;
  onContinue: () => void;
}

const hasContent = (html: string | undefined | null): boolean => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

const AnnotationEpilogueScreen: React.FC<AnnotationEpilogueScreenProps> = ({
  noteText,
  recordings,
  onContinue,
}) => {
  const { t } = useTranslation();
  const hasRecordings = recordings?.audio || recordings?.video;

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-amber-50">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-6 py-4 bg-amber-100 border-b-2 border-amber-300">
        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-200 shadow-sm flex-shrink-0">
          <PenLine className="w-4 h-4 text-amber-700" />
        </span>
        <div>
          <p className="text-sm font-semibold text-amber-900">
            {t("note_from_author") || "Pane Notes"}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {hasContent(noteText) && (
            <div
              className="prose prose-sm max-w-none text-amber-900 prose-headings:text-amber-900 prose-p:my-2 prose-ul:my-2 prose-ol:my-2"
              dangerouslySetInnerHTML={{ __html: noteText }}
            />
          )}

          {hasRecordings && (
            <div className="flex flex-wrap gap-3 pt-2">
              {recordings?.audio && (
                <RecordingPlayer
                  recording={recordings.audio}
                  type="audio"
                  readOnly
                />
              )}
              {recordings?.video && (
                <RecordingPlayer
                  recording={recordings.video}
                  type="video"
                  readOnly
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer — Continue button */}
      <div className="flex-shrink-0 px-6 py-4 bg-amber-100 border-t-2 border-amber-300 flex justify-end">
        <Button
          onClick={onContinue}
          className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
        >
          {t("continue") || "Continue"}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default AnnotationEpilogueScreen;
